import type {
  ListDiscountsResponse,
  ListPlanPricesResponse,
  ListPromosResponse,
} from "@wallyo/contracts/console/billing";
import { useEffect, useReducer, useState } from "react";
import { listDiscounts, listPlanPrices, listPromos } from "../../api/console-api";
import { useApi } from "../../api/context";
import { t } from "../../i18n";
import { useHash } from "../../navigation/use-hash";
import { useAlerts } from "../../shell/alerts";
import { PageTop } from "../../shell/PageTop";
import { useStaff } from "../../shell/staff-context";
import { BILLING_FEATURES, type BillingTarget } from "./billing-actions";
import type { BillingListState } from "./billing-model";
import { billingHref, parseBillingHash, BILLING_TABS, type BillingTab } from "./billing-route";
import { BillingWriteDialog } from "./BillingWriteDialog";
import { DiscountsView } from "./DiscountsView";
import { PricesView } from "./PricesView";
import { PromosView } from "./PromosView";

const LOADING = { status: "loading", data: null } as const;

/**
 * Tarifs et abonnements (VIEW_PAYMENTS, checked by the shell): the commercial
 * console of ADR-0018 §4a. `#billing` is the prices, `?tab=promos` the
 * promos, `?tab=discounts` the remises, and `?pro=<workerId>` opens the
 * remise form with that pro already in it.
 *
 * The console shows what the API answers and nothing else: `inForce`,
 * `state` and `subscriberCount` are the server's. Every write carries its
 * intent's Idempotency-Key and the version on screen.
 */
export function BillingScreen() {
  const hash = useHash();
  const { client } = useApi();
  const { me } = useStaff();
  const { tab, workerId } = parseBillingHash(hash);
  const has = (feature: (typeof BILLING_FEATURES)[keyof typeof BILLING_FEATURES]) => me.features.includes(feature);

  const [prices, setPrices] = useState<BillingListState<ListPlanPricesResponse>>(LOADING);
  const [promos, setPromos] = useState<BillingListState<ListPromosResponse>>(LOADING);
  const [discounts, setDiscounts] = useState<BillingListState<ListDiscountsResponse>>(LOADING);
  const [priceReads, readPrices] = useReducer((count: number) => count + 1, 0);
  const [promoReads, readPromos] = useReducer((count: number) => count + 1, 0);
  const [discountReads, readDiscounts] = useReducer((count: number) => count + 1, 0);
  const [target, setTarget] = useState<BillingTarget | null>(null);
  const alerts = useAlerts();

  // The prices carry the plan list every form's plan select offers, so they
  // are read whichever tab is open.
  useEffect(() => {
    let current = true;
    setPrices((was) => ({ status: "loading", data: was.data }));
    listPlanPrices(client).then(
      (data) => current && setPrices({ status: "ready", data }),
      (error: unknown) => current && setPrices((was) => ({ status: "failed", data: was.data, error })),
    );
    return () => {
      current = false;
    };
  }, [client, priceReads]);

  useEffect(() => {
    if (tab !== "promos") return;
    let current = true;
    setPromos((was) => ({ status: "loading", data: was.data }));
    listPromos(client).then(
      (data) => current && setPromos({ status: "ready", data }),
      (error: unknown) => current && setPromos((was) => ({ status: "failed", data: was.data, error })),
    );
    return () => {
      current = false;
    };
  }, [client, tab, promoReads]);

  useEffect(() => {
    if (tab !== "discounts") return;
    let current = true;
    setDiscounts((was) => ({ status: "loading", data: was.data }));
    listDiscounts(client).then(
      (data) => current && setDiscounts({ status: "ready", data }),
      (error: unknown) => current && setDiscounts((was) => ({ status: "failed", data: was.data, error })),
    );
    return () => {
      current = false;
    };
  }, [client, tab, discountReads]);

  const open = (next: BillingTarget) => setTarget(next);

  const reloadAll = () => {
    readPrices();
    readPromos();
    readDiscounts();
  };

  return (
    <>
      <PageTop title={t("screen.billing")} sub={t("sub.billing")} />
      <div className="tabs payment-tabs" role="tablist" aria-label={t("billing.tabs.aria")}>
        {BILLING_TABS.map((each) => (
          <TabLink key={each} tab={each} current={tab} workerId={workerId} />
        ))}
      </div>
      <Tab
        tab={tab}
        prices={prices}
        promos={promos}
        discounts={discounts}
        features={{
          prices: has(BILLING_FEATURES.prices),
          promos: has(BILLING_FEATURES.promos),
          discounts: has(BILLING_FEATURES.discounts),
        }}
        onOpen={open}
        onRetryPrices={readPrices}
        onRetryPromos={readPromos}
        onRetryDiscounts={readDiscounts}
      />
      <BillingWriteDialog
        target={target}
        plans={prices.data?.plans ?? []}
        workerId={workerId}
        canViewAccounts={me.features.includes("VIEW_ACCOUNTS_STAFF_FEATURE_ENABLEMENT")}
        onClose={() => setTarget(null)}
        onReload={() => {
          setTarget(null);
          reloadAll();
        }}
        onDone={(done) => {
          setTarget(null);
          alerts.ok(done.text);
          reloadAll();
        }}
      />
    </>
  );
}

function TabLink({ tab, current, workerId }: { tab: BillingTab; current: BillingTab; workerId: string | null }) {
  const on = tab === current;
  let className = "tab";
  if (on) className = "tab on";
  return (
    <a className={className} href={billingHref(tab, workerId)} role="tab" aria-selected={on}>
      {t(`billing.tab.${tab}` as const)}
    </a>
  );
}

function Tab({
  tab,
  prices,
  promos,
  discounts,
  features,
  onOpen,
  onRetryPrices,
  onRetryPromos,
  onRetryDiscounts,
}: {
  tab: BillingTab;
  prices: BillingListState<ListPlanPricesResponse>;
  promos: BillingListState<ListPromosResponse>;
  discounts: BillingListState<ListDiscountsResponse>;
  features: { prices: boolean; promos: boolean; discounts: boolean };
  onOpen: (target: BillingTarget) => void;
  onRetryPrices: () => void;
  onRetryPromos: () => void;
  onRetryDiscounts: () => void;
}) {
  if (tab === "promos") {
    return (
      <PromosView
        state={promos}
        canRunPromos={features.promos}
        onNewPromo={() => onOpen({ kind: "newPromo" })}
        onEndPromo={(promo) => onOpen({ kind: "endPromo", promo })}
        onRetry={onRetryPromos}
      />
    );
  }
  if (tab === "discounts") {
    return (
      <DiscountsView
        state={discounts}
        canOfferDiscounts={features.discounts}
        onNewDiscount={() => onOpen({ kind: "newDiscount" })}
        onEndDiscount={(discount) => onOpen({ kind: "endDiscount", discount })}
        onRetry={onRetryDiscounts}
      />
    );
  }
  return (
    <PricesView
      state={prices}
      canSetPrice={features.prices}
      now={new Date()}
      onSetPrice={() => onOpen({ kind: "setPrice" })}
      onRetry={onRetryPrices}
    />
  );
}
